import React, { useEffect, useState } from "react";
import Layout from "../../components/Sidebar";
import api from "../../api";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { CalendarDays, Plus, Clock } from "lucide-react";

const statusStyles = {
  pending: "bg-amber-100 text-amber-700 border-amber-200",
  approved: "bg-emerald-100 text-emerald-700 border-emerald-200",
  rejected: "bg-red-100 text-red-700 border-red-200",
};

const daysBetween = (s, e) => {
  if (!s || !e) return 0;
  const diff = new Date(e) - new Date(s);
  return Math.max(1, Math.ceil(diff / (1000 * 60 * 60 * 24)) + 1);
};

export default function LeaveRequests() {
  const [leaves, setLeaves] = useState([]);
  const [form, setForm] = useState({ start_date: "", end_date: "", reason: "" });
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const fetchLeaves = () => {
    api.get("/leaves").then((r) => setLeaves(r.data));
  };

  useEffect(() => { fetchLeaves(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.start_date || !form.end_date || !form.reason) {
      toast.error("All fields are required");
      return;
    }
    if (new Date(form.end_date) < new Date(form.start_date)) {
      toast.error("End date must be after start date");
      return;
    }
    setLoading(true);
    try {
      await api.post("/leaves", form);
      toast.success("Leave request submitted successfully");
      setForm({ start_date: "", end_date: "", reason: "" });
      setShowForm(false);
      fetchLeaves();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="p-8 page-enter">
        <div className="flex items-center justify-between mb-7">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Leave Requests</h1>
            <p className="text-slate-500 text-sm mt-1">Apply for time off and track your requests</p>
          </div>
          <Button
            onClick={() => setShowForm(!showForm)}
            className="bg-[#1a2332] hover:bg-[#2c3e50] text-white gap-2"
            data-testid="apply-leave-btn"
          >
            <Plus size={16} /> Apply for Leave
          </Button>
        </div>

        {/* Form */}
        {showForm && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 mb-6 animate-in fade-in slide-in-from-top-2 duration-300">
            <h3 className="font-semibold text-slate-900 mb-5 flex items-center gap-2">
              <CalendarDays size={18} className="text-[#38b6e8]" /> New Leave Request
            </h3>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <Label htmlFor="start" className="text-slate-700 font-medium text-sm">Start Date *</Label>
                  <Input
                    id="start"
                    type="date"
                    value={form.start_date}
                    min={new Date().toISOString().split("T")[0]}
                    onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                    className="mt-1.5 border-slate-200 focus-visible:ring-[#38b6e8]"
                    required
                    data-testid="leave-start-date"
                  />
                </div>
                <div>
                  <Label htmlFor="end" className="text-slate-700 font-medium text-sm">End Date *</Label>
                  <Input
                    id="end"
                    type="date"
                    value={form.end_date}
                    min={form.start_date || new Date().toISOString().split("T")[0]}
                    onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                    className="mt-1.5 border-slate-200 focus-visible:ring-[#38b6e8]"
                    required
                    data-testid="leave-end-date"
                  />
                </div>
              </div>
              {form.start_date && form.end_date && (
                <div className="mb-4 p-3 bg-sky-50 rounded-lg border border-sky-100">
                  <p className="text-sky-700 text-sm font-medium">
                    Duration: {daysBetween(form.start_date, form.end_date)} day(s)
                  </p>
                </div>
              )}
              <div className="mb-4">
                <Label htmlFor="reason" className="text-slate-700 font-medium text-sm">Reason *</Label>
                <Textarea
                  id="reason"
                  value={form.reason}
                  onChange={(e) => setForm({ ...form, reason: e.target.value })}
                  placeholder="Explain the reason for your leave..."
                  className="mt-1.5 border-slate-200 focus-visible:ring-[#38b6e8] resize-none"
                  rows={3}
                  required
                  data-testid="leave-reason"
                />
              </div>
              <div className="flex gap-3 justify-end">
                <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="border-slate-200">
                  Cancel
                </Button>
                <Button type="submit" disabled={loading} className="bg-[#1a2332] hover:bg-[#2c3e50] text-white" data-testid="submit-leave-btn">
                  {loading ? "Submitting..." : "Submit Request"}
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* History */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100">
            <h3 className="font-semibold text-slate-900">My Leave History</h3>
          </div>
          {leaves.length === 0 ? (
            <div className="p-12 text-center">
              <Clock size={36} className="text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 text-sm">No leave requests submitted yet</p>
            </div>
          ) : (
            <table className="w-full" data-testid="my-leaves-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  {["Duration", "Days", "Reason", "Status", "Submitted"].map((h) => (
                    <th key={h} className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-5 py-3.5">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {leaves.map((l) => (
                  <tr key={l.id} className="hover:bg-slate-50/60 transition-colors" data-testid={`leave-row-${l.id}`}>
                    <td className="px-5 py-4">
                      <p className="text-sm font-medium text-slate-800">{l.start_date}</p>
                      <p className="text-xs text-slate-400">to {l.end_date}</p>
                    </td>
                    <td className="px-5 py-4">
                      <span className="bg-slate-100 text-slate-700 text-xs font-semibold px-2.5 py-1 rounded-full">
                        {daysBetween(l.start_date, l.end_date)}d
                      </span>
                    </td>
                    <td className="px-5 py-4 text-slate-600 text-sm max-w-xs">
                      <p className="truncate">{l.reason}</p>
                      {l.rejection_reason && (
                        <p className="text-red-500 text-xs mt-0.5">Rejected: {l.rejection_reason}</p>
                      )}
                    </td>
                    <td className="px-5 py-4">
                      <Badge className={`${statusStyles[l.status]} border text-xs font-semibold capitalize`}>
                        {l.status}
                      </Badge>
                    </td>
                    <td className="px-5 py-4 text-slate-500 text-sm">
                      {new Date(l.created_at).toLocaleDateString("en-IN")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </Layout>
  );
}
